import { ShieldCheck, KeyRound, Phone } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { Img } from "./Img";

export const Guarantee = () => (
  <section id="guarantee" data-testid="guarantee-section" className="relative py-24 lg:py-32 surface-panel border-y border-white/[0.06] overflow-hidden noise-overlay">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Eyebrow>Zero-Risk Ownership</Eyebrow>
      </Reveal>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-7 items-stretch">
        <Reveal className="lg:col-span-7" delay={0.05}>
          <div
            data-testid="guarantee-banner"
            className="relative h-full rounded-2xl border border-accent/25 bg-gradient-to-br from-accent/[0.09] via-surface to-surface p-8 sm:p-12 overflow-hidden"
          >
            <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-accent/10 blur-3xl" aria-hidden="true" />
            <ShieldCheck size={44} className="text-accent" strokeWidth={1.4} aria-hidden="true" />
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1] text-balance">
              A Written <span className="accent-text">Lifetime Guarantee</span> on Every Panel We Touch
            </h2>
            <p className="mt-6 max-w-xl text-sm sm:text-base text-slate-300 leading-relaxed">
              We guarantee our craftsmanship and paint finishes for as long as you own the vehicle.
              Not a verbal promise — a signed, written warranty that travels with your car. If our
              work ever falls short of factory standard, we make it right. Period.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4">
              {[
                ["100%", "OEM-spec repair procedures"],
                ["~10%", "of shops hold I-CAR Gold Class"],
                ["Lifetime", "written warranty, in ink"],
              ].map(([stat, label]) => (
                <div key={label}>
                  <p className="font-display text-2xl sm:text-3xl font-bold text-accent">{stat}</p>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.16em] uppercase text-mute">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-5" delay={0.15}>
          <div
            data-testid="rental-module"
            className="group relative h-full rounded-2xl border border-white/[0.08] bg-surface overflow-hidden flex flex-col transition-all duration-500 hover:border-accent/30 hover:-translate-y-1.5"
          >
            <div className="relative h-44 sm:h-52 overflow-hidden flex items-center justify-center bg-surface">
              <Img
                name="rolls-royce-spectre-service-bay"
                alt="Rolls-Royce Spectre in the service bay — loaner and rental coordination at Westway Collision Center in Carrollton, TX"
                className="h-full w-auto max-w-full transition-transform duration-700 group-hover:scale-[1.06]"
                sizes="(min-width: 1024px) 40vw, 100vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/40 to-transparent" aria-hidden="true" />
            </div>
            <div className="p-7 sm:p-9 flex flex-col flex-1">
              <KeyRound size={30} className="text-accent" strokeWidth={1.6} aria-hidden="true" />
              <h3 className="mt-4 font-display text-xl sm:text-2xl font-semibold text-slate-100">
                On-Site Rental Coordination
              </h3>
              <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed flex-1">
                Drop your vehicle at our Carrollton facility and step straight into a premium rental.
                We coordinate directly with Enterprise and every major insurance carrier, so you stay
                on the move with zero friction and zero phone tag.
              </p>
              <a
                href="tel:+12148882886"
                data-testid="rental-call-cta"
                className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-accent hover:text-accentdeep transition-colors"
              >
                <Phone size={13} />
                Arrange my rental
                <span aria-hidden="true">&rarr;</span>
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
